import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import {
  Video, Upload, Radio, Music2, Play, Pause, Square, Trash2, Sparkles,
  Check, Camera, CameraOff, Mic, MicOff, SwitchCamera, Bell,
} from "lucide-react";
import { useStudioStage } from "../components/StudioStage";
import CreatorRecorder from "../components/CreatorRecorder";
import SegmentedVideo from "../components/SegmentedVideo";

const FILTERS = [
  { id: "none", label: "None", css: "none" },
  { id: "vibrant", label: "Vibrant", css: "saturate(1.6) contrast(1.1)" },
  { id: "mono", label: "Mono", css: "grayscale(1) contrast(1.1)" },
  { id: "warm", label: "Warm", css: "saturate(1.2) hue-rotate(-10deg) brightness(1.05)" },
  { id: "cool", label: "Cool", css: "saturate(1.1) hue-rotate(15deg) brightness(0.95)" },
  { id: "neon", label: "Neon", css: "saturate(2) contrast(1.2) hue-rotate(20deg)" },
];

export default function StudioPage({ defaultMode = "record" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState(defaultMode);

  // Keep the URL in sync so `/go-live` <→ `/studio` reflects the current tab.
  // This also makes the Studio / Go-Live shortcuts in the profile dropdown
  // feel snappy because nav() never remounts the page.
  useEffect(() => {
    const next = mode === "live" ? "/go-live" : "/studio";
    if (window.location.pathname !== next) {
      nav(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState(null);
  const [filter, setFilter] = useState("none");
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [hostName, setHostName] = useState(user?.name || "");
  const [notifyFollowers, setNotifyFollowers] = useState(true);
  const [notifyMessage, setNotifyMessage] = useState("");
  const [pinnedIds, setPinnedIds] = useState([]);  // products pinned to the live stream
  const [storeProducts, setStoreProducts] = useState([]);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const previewAudioRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [m, s, c] = await Promise.all([
        api.get("/music"),
        api.get("/seller/stores"),
        api.get("/categories"),
      ]);
      setTracks(m.data);
      setStores(s.data);
      setCategories(c.data);
      if (s.data[0]) setStoreId(s.data[0].store_id);
      if (s.data[0]?.category_id) setCategoryId(s.data[0].category_id);
    })();
  }, []);

  // preview selected track
  useEffect(() => {
    if (!previewAudioRef.current) return;
    if (trackId) {
      const tr = tracks.find(t => t.track_id === trackId);
      if (tr) {
        previewAudioRef.current.src = tr.url;
        previewAudioRef.current.volume = 0.5;
        previewAudioRef.current.play().catch(()=>{});
      }
    } else {
      previewAudioRef.current.pause();
    }
  }, [trackId, tracks]);

  // When the seller switches store, category, or enters the Go Live OR
  // Record/Upload modes, pull that store's products **scoped to the chosen
  // category** so they can be pinned/tagged to the upcoming asset.
  useEffect(() => {
    if (!storeId || !["live", "record", "upload"].includes(mode)) {
      setStoreProducts([]); return;
    }
    setPinnedIds([]);
    const params = new URLSearchParams({ store_id: storeId, limit: "200" });
    if (categoryId) params.set("category_id", categoryId);
    api.get(`/products?${params}`)
      .then(({ data }) => setStoreProducts(data || []))
      .catch(() => setStoreProducts([]));
  }, [storeId, mode, categoryId]);

  const fire = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  };

  if (!user || (user.role !== "seller" && user.role !== "admin")) {
    return <div className="max-w-xl mx-auto p-10 text-center">
      <div className="font-display text-2xl mb-4">Seller access required</div>
    </div>;
  }

  return (
    <div className="pb-24 md:pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest flex items-center gap-1"
            style={{ color: mode === "live" ? "#FF007F" : mode === "upload" ? "#00F0FF" : "#D4FF00" }}>
            <Sparkles size={12}/> {mode === "live" ? "Go Live · Broadcast now" : "Creator studio"}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase">
            {mode === "live"
              ? <>Go <span className="text-[#FF007F]">Live</span><span className="text-[#FF007F]">.</span></>
              : <>Make. Edit. Post<span className="text-[#FF007F]">.</span></>}
          </h1>
        </div>
      </div>

      {/* Mode tabs — colorful pills with ambient glow */}
      <div className="relative mb-6">
        <div className="absolute -inset-x-4 -inset-y-2 bg-gradient-to-br from-[#D4FF00]/5 via-[#00F0FF]/5 to-[#FF007F]/5 blur-2xl pointer-events-none"/>
        <div className="relative grid grid-cols-3 gap-2">
          <ModeBtn active={mode==="record"} onClick={()=>setMode("record")} icon={Video} label="Record" color="#D4FF00" testid="studio-mode-record"/>
          <ModeBtn active={mode==="upload"} onClick={()=>setMode("upload")} icon={Upload} label="Upload" color="#00F0FF" testid="studio-mode-upload"/>
          <ModeBtn active={mode==="live"} onClick={()=>setMode("live")} icon={Radio} label="Go Live" color="#FF007F" testid="studio-mode-live"/>
        </div>
      </div>

      {/* 1. Details form — first thing the seller fills in (store, category,
          title, host/caption). Same fields across all three modes so the
          flow is identical no matter which one is picked. */}
      <div className="card p-5 mb-4 grid gap-3" data-testid="studio-details-card">
        <div className="font-display font-bold uppercase text-sm">
          {mode === "live" ? "Live details" : "Post details"}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <select required className="input-dark" value={storeId} onChange={e=>setStoreId(e.target.value)} data-testid="studio-store">
            <option value="">Select store</option>
            {stores.map(s => <option key={s.store_id} value={s.store_id}>{s.name}</option>)}
          </select>
          <select className="input-dark" value={categoryId} onChange={e=>setCategoryId(e.target.value)} data-testid="studio-category">
            <option value="">Select category</option>
            {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
        </div>
        <input
          className="input-dark"
          placeholder={mode === "live" ? "Stream title (e.g. 'Friday sneaker drop')" : "Video title"}
          value={title}
          onChange={e=>setTitle(e.target.value)}
          data-testid="studio-title"
        />
        {mode === "live" ? (
          <input className="input-dark" placeholder="Host name" value={hostName}
            onChange={e=>setHostName(e.target.value)} data-testid="studio-host"/>
        ) : (
          <textarea className="input-dark" placeholder="Caption (optional)" rows={2}
            value={caption} onChange={e=>setCaption(e.target.value)} data-testid="studio-caption"/>
        )}
        {mode === "live" && (
          <div className="rounded-xl border border-[#3D2845] bg-[#1A0F1F] p-3 space-y-2.5" data-testid="notify-followers-card">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <button
                type="button"
                role="switch"
                aria-checked={notifyFollowers}
                onClick={() => setNotifyFollowers(v => !v)}
                data-testid="notify-followers-toggle"
                className={`relative w-11 h-6 rounded-full transition shrink-0 ${notifyFollowers ? "bg-[#D4FF00]" : "bg-[#3D2845]"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${notifyFollowers ? "translate-x-5" : ""}`}/>
              </button>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 font-bold text-sm">
                  <Bell size={14} className="text-[#D4FF00]"/> Notify my followers
                </span>
                <span className="block text-[11px] text-[#A1A1AA]">Everyone following your store gets a "you're live" alert.</span>
              </span>
            </label>
            {notifyFollowers && (
              <input
                className="input-dark text-sm"
                placeholder="Optional teaser message (e.g. 'Flash sale — 30% off for 1 hour!')"
                value={notifyMessage}
                maxLength={140}
                onChange={e => setNotifyMessage(e.target.value)}
                data-testid="notify-followers-message"
              />
            )}
          </div>
        )}
      </div>

      {/* 2. Tag products — appears as soon as a store + category are picked
          so the seller can pin them before recording / going live. The
          same picker is rendered for all three modes. */}
      {storeId && (
        <VideoProductPinner products={storeProducts}
          selected={pinnedIds} onChange={setPinnedIds}
          headline={mode === "live"
            ? "Tag products · viewers tap to shop during the stream"
            : "Tag products · viewers tap to shop from this video"}/>
      )}

      {/* 3. Filter ↔ Camera/Recorder/Preview ↔ Music — side-by-side so the
          seller can tweak FX while watching themselves. Stacks on mobile. */}
      <div className="mt-4 grid lg:grid-cols-[220px_minmax(0,1fr)_220px] gap-4 items-start">
        {/* LEFT — Filter rail */}
        <div className="card p-4 lg:max-h-[640px] lg:overflow-y-auto">
          <div className="font-display font-bold uppercase text-sm mb-3 flex items-center gap-1">
            <Sparkles size={14} className="text-[#D4FF00]"/> Filter
          </div>
          <div className="flex lg:flex-col gap-2 flex-wrap">
            {FILTERS.map(f => (
              <button
                key={f.id}
                onClick={()=>setFilter(f.id)}
                data-testid={`studio-filter-${f.id}`}
                className={`chip ${filter===f.id?"active":""} lg:w-full lg:text-left`}
              >{f.label}</button>
            ))}
          </div>
        </div>

        {/* CENTER — actual mode panel */}
        <div className="min-w-0">
          {mode === "record" && <RecordPanel filter={filter} trackId={trackId}
            onPublish={async (payload) => {
            setBusy(true);
            try {
              await api.post("/seller/studio/video", {
                store_id: storeId, title, caption, category_id: categoryId,
                music_id: trackId, filter,
                thumbnail_data_url: payload.thumbnail,
                product_ids: pinnedIds,
                fx: payload.fx,
              });
              fire("Video posted 🎬");
              setTimeout(() => nav(`/store/${storeId}`), 1200);
            } catch (e) {
              fire("Post failed: " + (e.response?.data?.detail || e.message));
            } finally { setBusy(false); }
          }} title={title} storeReady={!!storeId && !!title} />}

          {mode === "upload" && <UploadPanel filter={filter} trackId={trackId}
            onPublish={async (payload) => {
            setBusy(true);
            try {
              await api.post("/seller/studio/video", {
                store_id: storeId, title, caption, category_id: categoryId,
                music_id: trackId, filter,
                thumbnail_data_url: payload.thumbnail,
                video_url: payload.video_url,
                product_ids: pinnedIds,
              });
              fire("Video posted 🎬");
              setTimeout(() => nav(`/store/${storeId}`), 1200);
            } catch (e) {
              fire("Post failed: " + (e.response?.data?.detail || e.message));
            } finally { setBusy(false); }
          }} title={title} storeReady={!!storeId && !!title} />}

          {mode === "live" && <LivePanel filter={filter} trackId={trackId}
            title={title} storeId={storeId} categoryId={categoryId}
            onGoLive={async (payload) => {
              setBusy(true);
              try {
                const { data } = await api.post("/seller/studio/live", {
                  store_id: storeId, title, host_name: hostName || "host",
                  category_id: categoryId, music_id: trackId, filter,
                  thumbnail_data_url: payload.thumbnail,
                  pinned_product_ids: pinnedIds,
                  fx: payload.fx,
                  notify_followers: notifyFollowers,
                  notify_message: notifyMessage.trim() || null,
                });
                fire("You're live 🔴");
                setTimeout(() => nav(`/live/${data.stream_id}`), 900);
              } catch (e) {
                fire("Go live failed: " + (e.response?.data?.detail || e.message));
              } finally { setBusy(false); }
            }}
            storeReady={!!storeId && !!title && !!categoryId}
          />}
        </div>

        {/* RIGHT — Music rail */}
        <div className="card p-4 lg:max-h-[640px] lg:overflow-y-auto">
          <div className="font-display font-bold uppercase text-sm mb-3 flex items-center gap-1">
            <Music2 size={14} className="text-[#FF007F]"/> Pick music
          </div>
          <div className="flex lg:flex-col gap-3 lg:gap-2 overflow-x-auto lg:overflow-x-visible no-scrollbar pb-1 -mx-1 px-1">
            <button onClick={()=>setTrackId(null)}
              data-testid="studio-track-none"
              className={`shrink-0 lg:w-full text-center card p-2 ${!trackId?"border-[#D4FF00]":""}`}>
              <div className="aspect-square rounded bg-[#3D2845] flex items-center justify-center mb-1 lg:aspect-auto lg:py-3">
                <MicOff size={20} className="text-[#A1A1AA]"/>
              </div>
              <div className="text-[10px] font-bold uppercase">No music</div>
            </button>
            {tracks.map(t => (
              <button key={t.track_id} onClick={()=>setTrackId(t.track_id)}
                data-testid={`studio-track-${t.track_id}`}
                className={`shrink-0 lg:w-full text-center card p-2 ${trackId===t.track_id?"border-[#FF007F]":""}`}>
                <div className="aspect-square rounded overflow-hidden mb-1 relative">
                  <img src={t.cover} className="w-full h-full object-cover" alt=""/>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    {trackId===t.track_id
                      ? <Pause size={16} className="text-white fill-white"/>
                      : <Play size={16} className="text-white fill-white"/>}
                  </div>
                </div>
                <div className="text-[10px] font-bold uppercase truncate w-full">{t.title}</div>
                <div className="text-[9px] text-[#A1A1AA] truncate w-full">{t.artist}</div>
              </button>
            ))}
          </div>
          <audio ref={previewAudioRef} loop/>
        </div>
      </div>

      <p className="text-[11px] text-[#A1A1AA] mt-4">
        Tip: Video & stream hosting is <span className="text-[#FF007F] font-bold">MOCKED</span> in this MVP — your recording captures a thumbnail and metadata; playback uses a demo video. Plug in Cloudflare Stream / Mux to go live for real.
      </p>

      {toast && (
        <div className="fixed bottom-28 md:bottom-8 left-1/2 -translate-x-1/2 bg-[#D4FF00] text-black font-bold text-sm uppercase tracking-widest px-5 py-3 rounded-full z-50 shadow-2xl" data-testid="studio-toast">
          {toast}
        </div>
      )}

      {busy && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40">
          <div className="font-display text-xl text-[#D4FF00] animate-pulse">Publishing…</div>
        </div>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, color, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative overflow-hidden rounded-2xl p-4 flex flex-col items-center gap-2 transition group border ${
        active
          ? "scale-[1.02] shadow-2xl"
          : "border-[#3D2845] hover:border-white/30 hover:scale-[1.02]"
      }`}
      style={active ? {
        background: `linear-gradient(135deg, ${color}44 0%, ${color}0A 55%, transparent 100%)`,
        borderColor: color,
        boxShadow: `0 8px 24px ${color}33, inset 0 0 0 1px ${color}55`,
      } : {
        background: "#261534",
      }}
    >
      {/* Animated glow ring on active */}
      {active && (
        <span
          className="absolute inset-0 rounded-2xl pointer-events-none opacity-60"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${color}55 0%, transparent 60%)`,
          }}
        />
      )}
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center transition group-hover:rotate-6 relative z-10"
        style={{
          background: active
            ? `linear-gradient(135deg, ${color} 0%, ${color}AA 100%)`
            : `${color}1F`,
          boxShadow: active ? `0 4px 12px ${color}66` : "none",
        }}
      >
        <Icon
          size={20}
          strokeWidth={2.5}
          style={{ color: active ? "#000" : color }}
        />
      </div>
      <div
        className="font-display font-black uppercase text-sm tracking-wider relative z-10"
        style={{ color: active ? color : "#FAFAFA" }}
      >
        {label}
      </div>
    </button>
  );
}

/* ============================================================== */
/* RECORD — full-featured creator recorder (front/back camera,
 *   pause/resume, multi-clip, timer, color + beauty + AR + bg-blur). */
function RecordPanel({ trackId, onPublish, storeReady }) {
  return (
    <CreatorRecorder
      storeReady={storeReady}
      onPosted={(payload) => onPublish(payload)}
    />
  );
}

/* ============================================================== */
/* UPLOAD */
function UploadPanel({ filter, trackId, onPublish, title, storeReady }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const filterCss = FILTERS.find(f=>f.id===filter)?.css || "none";

  const onFile = (f) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const captureThumbnail = () => new Promise((resolve) => {
    const v = document.createElement("video");
    v.src = preview;
    v.muted = true;
    v.addEventListener("loadeddata", () => { v.currentTime = 0.1; });
    v.addEventListener("seeked", () => {
      const canvas = document.createElement("canvas");
      canvas.width = 360; canvas.height = 640;
      const ratio = Math.min(canvas.width/v.videoWidth, canvas.height/v.videoHeight);
      const w = v.videoWidth*ratio, h = v.videoHeight*ratio;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#000"; ctx.fillRect(0,0,canvas.width,canvas.height);
      ctx.filter = filterCss;
      ctx.drawImage(v, (canvas.width-w)/2, (canvas.height-h)/2, w, h);
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    }, { once: true });
    v.play().then(()=>v.pause()).catch(()=>{});
  });

  const handlePost = async () => {
    const thumbnail = preview ? await captureThumbnail() : null;
    // Read file as data-URL so we can persist the actual upload (small clips).
    let video_url = null;
    if (file && file.size <= 12 * 1024 * 1024) {
      video_url = await new Promise((res) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result || ""));
        r.readAsDataURL(file);
      });
    }
    onPublish({ thumbnail, video_url });
  };

  return (
    <div className="card overflow-hidden">
      <div className="relative mx-auto bg-black" style={{ filter: filterCss, aspectRatio: "9/16", width: "100%", maxWidth: "min(540px, 92vw)" }}>
        {preview ? (
          <video ref={videoRef} src={preview} controls loop className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-center p-6">
            <div>
              <Upload size={36} strokeWidth={2.5} className="mx-auto text-[#00F0FF] mb-3"/>
              <div className="font-display font-bold uppercase">Drop a video here</div>
              <div className="text-xs text-[#A1A1AA] mt-1">or tap below to browse</div>
            </div>
          </div>
        )}
      </div>
      <input type="file" accept="video/*" ref={inputRef} hidden onChange={e=>e.target.files?.[0] && onFile(e.target.files[0])} data-testid="upload-input"/>
      <div className="p-4 flex flex-wrap items-center gap-2 justify-center bg-[#261534]">
        <button onClick={()=>inputRef.current?.click()} data-testid="upload-pick-btn" className="btn-secondary"><Upload size={14} className="inline mr-1"/>{preview ? "Change file" : "Choose file"}</button>
        {preview && (
          <button onClick={handlePost} disabled={!storeReady} data-testid="upload-post-btn" className="btn-primary">
            <Check size={14} className="inline mr-1"/>Post video
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================================================== */
/* LIVE */
function LivePanel({ filter, trackId, onGoLive, storeReady, title, storeId, categoryId }) {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [front, setFront] = useState(true);
  const [micEnabled, setMicEnabled] = useState(true);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [netQuality, setNetQuality] = useState("ok");   // ok | weak | offline
  const filterCss = FILTERS.find(f=>f.id===filter)?.css || "none";
  const stage = useStudioStage();

  // Live network check
  useEffect(() => {
    const update = () => {
      if (!navigator.onLine) { setOnline(false); setNetQuality("offline"); return; }
      setOnline(true);
      const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const t = c?.effectiveType;
      if (t && (t === "slow-2g" || t === "2g")) setNetQuality("weak");
      else setNetQuality("ok");
    };
    update();
    window.addEventListener("online",  update);
    window.addEventListener("offline", update);
    const c = navigator.connection;
    c?.addEventListener?.("change", update);
    return () => {
      window.removeEventListener("online",  update);
      window.removeEventListener("offline", update);
      c?.removeEventListener?.("change", update);
    };
  }, []);

  // Re-attach stream whenever React mounts a new <video>
  const setVideoEl = (el) => {
    videoRef.current = el;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      el.muted = true;
      el.play().catch(()=>{});
    }
  };

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: front ? "user" : "environment" },
        audio: true,
      });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        videoRef.current.play().catch(()=>{});
      }
    } catch (e) { alert("Camera permission required"); }
  };

  const stopStream = () => {
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
  };
  useEffect(() => () => stopStream(), []);

  const toggleMic = () => {
    if (!stream) return;
    const next = !micEnabled;
    stream.getAudioTracks().forEach(t => { t.enabled = next; });
    setMicEnabled(next);
  };

  // Pre-flight checklist state
  const hasCamera = !!stream && stream.getVideoTracks().some(t => t.readyState === "live");
  const hasMic    = !!stream && micEnabled && stream.getAudioTracks().some(t => t.readyState === "live" && t.enabled);
  const hasStore  = !!storeId;
  const hasTitle  = !!(title && title.trim());
  const hasCat    = !!categoryId;
  const hasNet    = netQuality !== "offline";
  const checklist = [
    { id: "store",  label: "Store selected",    ok: hasStore,  hint: "Pick / create a store in the form below" },
    { id: "title",  label: "Stream title",      ok: hasTitle,  hint: "Add a punchy title in the form below" },
    { id: "cat",    label: "Category set",      ok: hasCat,    hint: "Choose a category in the form below" },
    { id: "cam",    label: "Camera on",         ok: hasCamera, hint: "Tap 'Turn on camera' to enable preview" },
    { id: "mic",    label: "Mic on",            ok: hasMic,    hint: "Unmute the mic — viewers need to hear you" },
    { id: "net",    label: netQuality === "weak" ? "Internet — weak" : "Internet OK",
                    ok: hasNet, warn: netQuality === "weak",
                    hint: netQuality === "offline" ? "You're offline" : "Slow connection — try Wi-Fi" },
  ];
  const passed = checklist.filter(c => c.ok).length;
  const allOK  = checklist.every(c => c.ok);
  const ringDeg = (passed / checklist.length) * 360;

  const handleGoLive = async () => {
    const thumbnail = stream ? await stage.captureThumbnail(videoRef.current) : null;
    onGoLive({ thumbnail, fx: stage.metadata });
  };

  // When a custom BG is selected, run the camera through MediaPipe selfie-
  // segmentation so the bg sits BEHIND the host (not letterboxed).
  const bgGradient = stage.bg?.gradient || null;
  const bgImage    = stage.bgUrl || stage.bg?.image || null;
  const bgActive   = !!(bgGradient || bgImage);

  const videoNode = (
    <>
      {bgActive && stream ? (
        <SegmentedVideo
          stream={stream}
          mirror={front}
          filterCss={filterCss}
          gradient={bgGradient}
          imageUrl={bgImage}
          className="w-full h-full object-cover"
          onCanvas={(c) => { videoRef.current = c; }}
        />
      ) : (
        <video ref={setVideoEl} playsInline autoPlay muted className="w-full h-full object-cover"/>
      )}
      {!stream && (
        <div className="absolute inset-0 flex items-center justify-center text-center p-6 bg-black/60">
          <div>
            <Radio size={36} strokeWidth={2.5} className="mx-auto text-[#FF007F] mb-3"/>
            <div className="font-display font-bold uppercase">Turn on camera</div>
            <div className="text-xs text-[#A1A1AA] mt-1">Preview before going live</div>
          </div>
        </div>
      )}
    </>
  );

  const liveBadge = stream ? (
    <div className="absolute top-3 left-3 z-10 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
      <Camera size={12}/> Preview
    </div>
  ) : null;

  return (
    <div className="card overflow-hidden" data-testid="live-panel">
      <stage.Stage videoNode={videoNode} filterCss={filterCss} liveBadge={liveBadge}/>

      {/* Pre-flight checklist */}
      <div className="p-4 sm:p-5 bg-[#1A0F1F] border-t border-[#3D2845]" data-testid="live-checklist">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative w-10 h-10 shrink-0">
            <div className="absolute inset-0 rounded-full" style={{
              background: `conic-gradient(${allOK ? "#D4FF00" : "#FF007F"} ${ringDeg}deg, #3D2845 0deg)`,
            }}/>
            <div className="absolute inset-1 rounded-full bg-[#1A0F1F] flex items-center justify-center font-display font-black text-xs">
              {passed}/{checklist.length}
            </div>
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA]">Pre-flight checklist</div>
            <div className="font-display font-bold text-sm sm:text-base">
              {allOK
                ? <span className="text-[#D4FF00]">Ready to go live!</span>
                : `${checklist.length - passed} item${checklist.length - passed === 1 ? "" : "s"} left`}
            </div>
          </div>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {checklist.map(c => (
            <li key={c.id}
              data-testid={`preflight-${c.id}`}
              data-ok={c.ok ? "yes" : "no"}
              className={`flex items-start gap-2 p-2 rounded-lg border transition ${
                c.ok
                  ? c.warn ? "bg-[#FFA500]/5 border-[#FFA500]/30" : "bg-[#D4FF00]/5 border-[#D4FF00]/30"
                  : "bg-[#FF007F]/5 border-[#FF007F]/20"
              }`}>
              <span className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${
                c.ok ? (c.warn ? "bg-[#FFA500] text-black" : "bg-[#D4FF00] text-black") : "bg-[#FF007F]/30 text-[#FF007F]"
              }`}>
                {c.ok ? <Check size={10} strokeWidth={3.5}/> : <span className="text-[8px]">!</span>}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold">{c.label}</div>
                {!c.ok && <div className="text-[10px] text-[#A1A1AA]">{c.hint}</div>}
                {c.ok && c.warn && <div className="text-[10px] text-[#FFA500]">{c.hint}</div>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-4 flex flex-wrap items-center gap-2 justify-center bg-[#261534]">
        {!stream ? (
          <button onClick={start} data-testid="live-start-cam" className="btn-primary">
            <Camera size={14} className="inline mr-1.5"/>Turn on camera
          </button>
        ) : (
          <>
            <button onClick={()=>{setFront(!front); stopStream(); setTimeout(start, 100);}} className="btn-secondary">
              <SwitchCamera size={14} className="inline mr-1"/>Flip
            </button>
            <button onClick={toggleMic} data-testid="live-mic-toggle" className="btn-secondary">
              {micEnabled ? <Mic size={14} className="inline mr-1"/> : <MicOff size={14} className="inline mr-1"/>}
              {micEnabled ? "Mic on" : "Mic off"}
            </button>
            <button onClick={handleGoLive} disabled={!allOK}
              data-testid="live-go-btn"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              style={allOK ? {background:"#FF007F",color:"#fff"} : {}}>
              <Radio size={14} className="inline mr-1"/>{allOK ? "Go live now" : `Go live (${checklist.length - passed} left)`}
            </button>
          </>
        )}
      </div>

      {/* Pin products is now rendered ABOVE the camera at parent level */}

      <stage.Toolbar/>
    </div>
  );
}

function VideoProductPinner({ products, selected, onChange, headline }) {
  return (
    <div className="card mt-4" data-testid="video-pin-products">
      <LiveProductPinner products={products} selected={selected} onChange={onChange}
        headline={headline}/>
    </div>
  );
}

function LiveProductPinner({ products, selected, onChange, headline }) {
  const [q, setQ] = useState("");
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return products;
    return products.filter(p => (p.name || "").toLowerCase().includes(needle));
  }, [products, q]);
  const toggle = (pid) => {
    onChange(selected.includes(pid) ? selected.filter(x => x !== pid) : [...selected, pid]);
  };
  return (
    <div className="p-4 sm:p-5 bg-[#1A0F1F] border-t border-[#3D2845]"
         data-testid="live-pin-products">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] flex-1">
          {headline || "Tag products · buyers tap to shop from this stream"}
        </div>
        {selected.length > 0 && (
          <span className="text-[10px] font-bold text-[#D4FF00]"
            data-testid="live-pin-count">{selected.length} pinned</span>
        )}
      </div>
      <input className="input-dark py-1 text-xs w-full mb-2"
        placeholder={products.length === 0 ? "No products in this store yet — add some first."
          : `Search ${products.length} products…`}
        value={q} onChange={e => setQ(e.target.value)}
        data-testid="live-pin-search"
        disabled={products.length === 0}/>
      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1"
           data-testid="live-pin-list">
        {filtered.slice(0, 60).map(p => {
          const on = selected.includes(p.product_id);
          return (
            <button key={p.product_id} type="button"
              onClick={() => toggle(p.product_id)}
              data-testid={`live-pin-${p.product_id}`}
              className={`chip text-xs flex items-center gap-1.5 ${on ? "active" : ""}`}>
              {p.images?.[0] && (
                <img src={p.images[0]} alt="" className="w-4 h-4 rounded object-cover"/>
              )}
              <span className="truncate max-w-[140px]">{p.name}</span>
              {on && <Check size={10}/>}
            </button>
          );
        })}
        {filtered.length === 0 && products.length > 0 && (
          <div className="text-xs text-[#A1A1AA]">No products match "{q}".</div>
        )}
      </div>
    </div>
  );
}
